import React, { useState } from "react";
import moment from "moment";
import { ThemeConsumer } from "styled-components";
import { cover, rgba } from "polished";

import { graphql, StaticQuery } from "gatsby";
import { RichText as PrismicRichText } from "prismic-reactjs";

import {
  AbrioFact,
  FlexGrid,
  H6,
  PageHeader,
  SectionWrapper,
  SEO,
  Share,
  Text,
  Box,
  Button,
  FlexRow
} from "src/components";

import serializer from "src/utils/serializer";
import linkResolver from "src/utils/linkResolver";

const BlogItem = ({ color, date, video, image }) => (
  <ThemeConsumer>
    {theme => (
      <article
        css={`
          padding: 24px 12px 24px;
          border-bottom: 2px solid ${props => props.theme.colors.text.default};
          background-color: ${rgba(theme.colors.brand[color], 1 / 10)};
          &:first-child {
            border-top: 2px solid ${props => props.theme.colors.text.default};
          }
        `}
      >
        <H6
          css={`
            font-weight: 400;
          `}
        >
          {date}
        </H6>
        <Share />
        {video && (
          <div
            css={`
              max-width: 512px;
            `}
          >
            <div
              dangerouslySetInnerHTML={{ __html: video }}
              css={`
                position: relative;
                margin-top: 16px;
                padding-bottom: ${(270 / 480) * 100}%;
                iframe {
                  ${cover()};
                  width: 100%;
                  height: 100%;
                }
              `}
            />
          </div>
        )}
        {image && !video && (
          <div
            css={`
              max-width: 512px;
              margin-top: 16px;
            `}
          >
            <img
              src={image}
              css={`
                width: 100%;
              `}
            />
          </div>
        )}
      </article>
    )}
  </ThemeConsumer>
);

BlogItem.defaultProps = {
  color: "blue"
};

function MainContent({ description, posts }) {
  const [count, setCount] = useState(3);

  function loadMore() {
    setCount(count + 3);
  }

  return (
    <article
      css={`
        width: 67%;
        @media (max-width: 64rem) {
          width: 100%;
        }
      `}
    >
      <section
        css={`
          margin: 64px 0;
        `}
      >
        <Text fontSize={3}>{description}</Text>
      </section>
      {posts && (
        <section>
          {posts
            .sort((a, b) =>
              moment(a.date).unix() > moment(b.date).unix() ? -1 : 1
            )
            .slice(0, count)
            .map((post, index) => (
              <BlogItem
                date={moment(post.date).format("MMMM DD, YYYY")}
                video={post.video && post.video.html && post.video.html}
                image={post.image && post.image.url && post.image.url}
                color={
                  index % 4 === 0
                    ? "blue"
                    : index % 4 === 1
                    ? "green"
                    : index % 4 === 2
                    ? "yellow"
                    : "pink"
                }
              />
            ))}
          {posts.length > count && (
            <FlexRow justifyContent="center" my={4}>
              <Button children={`Load more`} onClick={loadMore} />
            </FlexRow>
          )}
        </section>
      )}
    </article>
  );
}

const Body = ({ description, posts, factNumber, factIllustration, fact }) => (
  <SectionWrapper>
    <FlexGrid gutterX={[8]} gutterY={[6]}>
      <MainContent description={description} posts={posts} />
      {factIllustration && (
        <AbrioFact
          number={factNumber}
          children={fact}
          color="brand.blue"
          type={factIllustration}
        />
      )}
    </FlexGrid>
  </SectionWrapper>
);

const MayorsBlog = ({ preview }) => (
  <StaticQuery
    query={graphql`
      {
        prismicMayorsBlog {
          dataString
        }
      }
    `}
    render={data => {
      let page = JSON.parse(data.prismicMayorsBlog.dataString);
      if (preview) {
        page = preview;
      }
      return (
        <>
          <SEO
            image={page.seo_image && page.seo_image.url}
            title={page.seo_title && PrismicRichText.asText(page.seo_title)}
            description={
              page.seo_description &&
              PrismicRichText.asText(page.seo_description)
            }
          />
          <PageHeader
            heading={page.header && PrismicRichText.asText(page.header)}
            color="brand.blue"
          />
          <Body
            description={
              page.page_description &&
              PrismicRichText.render(
                page.page_description,
                linkResolver,
                serializer
              )
            }
            factIllustration={page.fact_illustration}
            factNumber={page.fact_number}
            fact={
              page.fact &&
              PrismicRichText.render(page.fact, linkResolver, serializer)
            }
            posts={page.posts}
          />
        </>
      );
    }}
  />
);

export default MayorsBlog;
